import { AccountSettings } from "./common/account-settings";
import { Affiliate } from "./common/affiliate";
import { AnnualValidation } from "./common/annual-validation";
import { Blog } from "./common/blog";
import { Campaign } from "./common/campaign";
import { CommonContent } from "./common/common-content";
import { CustomerAssessments } from "./common/customer-assessments";
import { DataProtection } from "./common/data-protection";
import { DocumentValidation } from "./common/document-validation";
import { FraudPrevention } from "./common/fraud-prevention";
import { Geofencing } from "./common/geofencing";
import { Home } from "./common/home";
import { KycVerification } from "./common/kyc-verification";
import { Loyalty } from "./common/loyalty";
import { MultiSessionLimit } from "./common/multi-session-limit";
import { OneTrust } from "./common/one-trust";
import { RealTimeNotifications } from "./common/real-time-notifications";
import { Registration } from "./common/registration";
import { CommonResponsibleGaming } from "./common-responsible-gaming";

export interface Common {
	/**
	 * Parameters for setting up account settings features.
	 */
	accountSettings: AccountSettings;
	/**
	 * Parameters for setting up affiliate features.
	 */
	affiliate: Affiliate;
	/**
	 * Parameters for setting up blog features.
	 */
	blog: Blog;
	/**
	 * Parameters for setting up campaign features.
	 */
	campaign: Campaign;
	/**
	 * Parameters for setting up content features.
	 */
	content: CommonContent;
	/**
	 * Cookie notice feature settings
	 */
	cookieNotice: {
		/**
		 * Enables or disables the cookie notice feature
		 */
		isEnabled?: boolean;
	};
	/**
	 * customer assessments settings
	 */
	customerAssessments?: CustomerAssessments;
	/**
	 * Data Protection sub features toggles.
	 */
	dataProtection: DataProtection;
	/**
	 * Parameters for setting up fraud prevention features.
	 */
	fraudPrevention: FraudPrevention;
	/**
	 * Parameters for Geofencing feature
	 */
	geofencing?: Geofencing;
	/**
	 * Parameters for setting up iosNativeBlock features.
	 */
	iosNativeBlock: {
		/**
		 * Enabled or disables IOS native block feature
		 */
		enabled?: boolean;
	};
	/**
	 * Whether marketing renewal dialog appears after login or not.
	 */
	isMarketingRenewalDialogEnabled?: boolean;
	/**
	 * Is sga enabled boolean, **to be removed**.
	 */
	isSgaEnabled?: boolean;
	/**
	 * Post registration kyc verification feature
	 */
	kycVerification?: KycVerification;
	/**
	 * Post registration annual validation notification
	 */
	annualValidation?: AnnualValidation;
	/**
	 * Post Registration document expiration notification
	 */
	documentValidation?: DocumentValidation;
	/**
	 * Parameters for limits reminder flow
	 */
	limitsReminder?: {
		/**
		 * Indicates if limit reminder modal is enabled or not
		 */
		isLimitsReminderEnabled?: boolean;
		/**
		 * Enable or disable last login timestamp after logging in
		 */
		isLastLoginTimestampEnabled?: boolean;
		/**
		 * Indicate when modal will be closed after logout (value in milliseconds)
		 */
		limitsModalTimeout?: number;
	};
	/**
	 * Login features
	 */
	login?: {
		/**
		 * Login alert features
		 */
		loginUiAlert?: {
			/**
			 * Enable or disable seconds for last login date and time
			 */
			isLastLoginDateAndTimeSecondsEnabled?: boolean;
		};
		/**
		 * Enable or disable the verification required after 40 days feature
		 */
		enableLoginFortyDaysReview: boolean;
		/**
		 * Enable or disable redirection on poker client
		 */
		pokerLoginRedirectionDisallowed?: boolean;
		/**
		 * Enable or disable profile completeness page instead of customer migration page on poker client
		 */
		pokerLoginProfileCompletenessPageEnabled?: boolean;
	};
	/**
	 * New Login Flow enable or disable
	 */
	loginFlow?: {
		/**
		 * List of the login flow steps
		 */
		newLoginFlow?: {
			/**
			 * The step name
			 */
			name?: string;
			data: Record<string, any>;
		}[];
	};
	/**
	 * Login yearly review setting
	 */
	loginYearlyReview?: {
		/**
		 * Enables and disables the login yearly review
		 */
		isEnabled?: boolean;
	};
	/**
	 * Google OIDC authentication related configuration
	 */
	googleLogin?: {
		isEnabled: boolean;
	};
	/**
	 * Parameters for setting up loyalty features.
	 */
	loyalty: Loyalty;
	/**
	 * Parameters for setting up loyalty content features.
	 */
	loyaltyContent: {
		/**
		 * Endpoints
		 */
		endpoints?: {
			/**
			 * Earn points endpoint URL
			 */
			earnPoints?: string;
			/**
			 * Member levels endpoint URL
			 */
			memberLevels?: string;
		};
	};
	/**
	 * Multi Session Limit feature settings
	 */
	multiSessionLimit: MultiSessionLimit;
	/**
	 * OneTrust feature settings
	 */
	oneTrust: OneTrust;
	/**
	 * Parameters for setting up payment features.
	 */
	payment: {
		isDepositLimitHintEnabled: boolean;
		/**
		 * A list of markets where showing the snackbar that appears if the customer decides to deposit without bonus is disabled
		 */
		showDepositWithoutBonusSnackbarDisabledMarkets?: string[];
		/**
		 * Toggles showing the notification reminding an unverified user to verify before depositing
		 */
		showVerifyNotification?: boolean;
		/**
		 * Determines if the quick deposit widget is shown on Sportsbook.
		 */
		isSportsbookQuickDepositEnabled?: boolean;
		endpoints?: {
			knowYourCustomer?: string;
			payment?: string;
			pendingWithdrawals?: string;
		};
		/**
		 * Determines if the iframe title and close button are hidden
		 */
		hideTitleAndCloseButton?: boolean;
	};
	/**
	 * Parameters for setting up phone verification features.
	 */
	phoneVerification: {
		endpoints: {
			/**
			 * Phone verification endpoint URL
			 */
			phoneVerification?: string;
		};
	};
	/**
	 * Parameters for setting up SAR features.
	 */
	personalData: {
		endpoints: {
			/**
			 * Personal Data SAR {Subject Access Requests} endpoint URL
			 */
			personalData?: string;
		};
	};
	/**
	 * Parameters for setting up prop message feature.
	 */
	propMessage?: {
		/**
		 * Prop Message A/B test experiment ID
		 */
		experimentID?: string;
		/**
		 * Enables the Prop Message feature
		 */
		isEnabled?: boolean;
	};
	/**
	 * Parameters for setting up real time notifications
	 */
	realTimeNotifications?: RealTimeNotifications;
	/**
	 * Parameters for setting up registration features.
	 */
	registration: Registration;
	/**
	 * Parameters for setting up home endpoints.
	 */
	home: Home;
	/**
	 * Parameters for setting up registration with personal number features.
	 */
	registrationWithPersonalNumber: {
		endpoints?: {
			/**
			 * Personal number verification endpoint URL
			 */
			personalNumberVerification?: string;
		};
	};
	/**
	 * Parameters for setting up responsible gaming features.
	 */
	responsibleGaming: CommonResponsibleGaming;
	/**
	 * Parameters for setting up reward features.
	 */
	reward: {
		/**
		 * Enables or disables reward feature
		 */
		enabled?: boolean;
		/**
		 * Endpoints
		 */
		endpoints?: {
			/**
			 * Get reward endpoint URL
			 */
			getReward?: string;
			/**
			 * Rewards endpoint URL
			 */
			rewards?: string;
			/**
			 * Rewards state endpoint URL
			 */
			rewardsState?: string;
			/**
			 * Rewards update endpoint URL
			 */
			rewardsUpdate?: string;
		};
	};
	/**
	 * Parameters for setting up seamless registration features.
	 */
	seamlessRegistration: {
		/**
		 * Endpoints
		 */
		endpoints?: {
			/**
			 * Seamless registration endpoint URL
			 */
			seamlessRegistration?: string;
		};
	};
	/**
	 * Parameters for setting up set missing password features
	 */
	setMissingPassword: {
		/**
		 * Endpoint that sets the users missing password
		 */
		endpoint?: string;
		/**
		 * Toggles on and off, if a user can set its missing password
		 */
		isEnabled?: boolean;
		/**
		 * Cache max age for skipping set password in days
		 */
		skipCacheMaxAge?: number;
		/**
		 * The time in milliseconds until the user has to set its password
		 */
		timeout?: number;
	};
	/**
	 * Parameters for setting up sms notification settings features.
	 */
	smsNotificationSettings: {
		endpoints?: {
			smsNotifications?: string;
		};
	};
	/**
	 * Parameters for setting up support features.
	 */
	support: {
		/**
		 * Endpoints
		 */
		endpoints?: {
			/**
			 * Live chat url endpoint URL
			 */
			liveChatUrl?: string;
		};
	};
	/**
	 * Parameters for configuring terms and conditions features.
	 */
	termsAndConditions?: {
		/**
		 * A list of terms and conditions endpoints
		 */
		endpoints?: {
			/**
			 * History endpoint url
			 */
			history?: string;
			/**
			 * Latest version endpoint url
			 */
			latestVersion?: string;
			/**
			 * Latest accepted content endpoint url
			 */
			latestAcceptedContent?: string;
		};
		/**
		 * Enable or disable history tab.
		 */
		historyEnabled?: boolean;
		/**
		 * Enable or disable downloading latest accepted terms and agreements document.
		 */
		isLatestAcceptedContentEnabled?: boolean;
		/**
		 * Enable or disable showing the whole terms and conditions in a dialog.
		 */
		showFullContentInDialog?: boolean;
	};

	/**
	 * Parameters for configuring initial home page
	 */
	alternativeMainPage: {
		/**
		 * Enable or disable home page as the main starting page
		 */
		isEnabled: boolean;
		/**
		 * Route of new main page
		 */
		path: string;
		/**
		 * Enabled or disables feature for specific market
		 */
		supportedMarkets?: string[];
	};

	/**
	 * Parameters for configuring national data features.
	 */
	nationalData?: {
		/**
		 * A list of national data endpoints
		 */
		endpoints?: {
			/**
			 * Birth provinces url
			 */
			birthProvinces?: string;
			/**
			 * Birth cities endpoint url prefix
			 */
			birthCitiesPrefix?: string;
			/**
			 * Country with cities endpoint url
			 */
			countryCities?: string;
			/**
			 * Tax code fragment by country code endpoint url
			 */
			taxCodeFragmentByCountry?: string;
			/**
			 * Country code by tax code fragment endpoint url
			 */
			countryByTaxCodeFragment?: string;
		};
		/**
		 * Flag that indicates that the city names should include state codes.
		 */
		addStateCodeToCities?: boolean;
	};

	/**
	 * Parameters for configuring alternative layout.
	 */
	alternativeLayout: {
		/**
		 * Enabled or disables new buttons style
		 */
		alternativeButtonsEnabled: boolean;
	};
	/**
	 * Parameters for configuring app download.
	 */
	appDownload?: {
		/**
		 * A list of app download endpoints
		 */
		endpoints?: {
			/**
			 * Endpoint for downloading app from App Store (iOS)
			 */
			appStoreUrl?: string;
			/**
			 * Endpoint for downloading app from Google Play (Android)
			 */
			androidAppUrl?: string;
		};
	};
	/**
	 * Parameters for configuring sport blog.
	 */
	sportBlog?: {
		/**
		 * Enabled or disables blog feature
		 */
		isEnabled?: boolean;
	};
	/**
	 * Parameters for setting up yearly financial report features
	 */
	yearlyFinancialReport: {
		/**
		 * Enable or disable page
		 */
		isEnabled?: boolean;
	};

	gameRoundHistory: {
		/**
		 * Providers for which Game Round History should show data only for current game
		 */
		gameRoundHistoryByGameId: {
			providers: string[];
		};
	};

	/**
	 * Parameters for age verification dialog
	 */
	ageVerificationDialog: {
		/**
		 * List of jurisdictions where enabled the dialog
		 */
		enabledJurisdictions: string[];
		/**
		 * List of markets where enabled the dialog
		 */
		enabledMarkets: string[];
	};

	/**
	 * Indicates whether the Brand is Light or Dark Themed
	 */
	productTheme?: ProductTheme;
}

export enum ProductTheme {
	Light = "Light",
	Dark = "Dark",
}
